// 사용중인 화면 : 원티드 작성페이지 캘린더 / 근무표 보기 > 내 근무표 캘린더

import { useMemo } from "react";
import type * as type from "@vTypes";
import { cn } from "@utils/cn.ts";

import Card_date from "./Card_date";

interface CalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_calendar {
  [date: string]: CalendarEntry;
}
interface Type_ShiftEntry {
  shift: string;
  comment: string | null;
}
type CalendarData = Record<string, Type_ShiftEntry>;

function TableSelectDate({
  activeDate,
  holidayDates,
  availableShifts,
  formattedCalendarData,
  selectedCalendarData,
  setSelectedCalendarData,
  submittedStatus,

  selectedShiftType,

  isExistWanted,
  myWeeklyOff,
  enableHighlightNew = false,
  enableCommentInput = false,
  onShiftChangeRequest,
  dailyMaxRequests,
  dailyRequestCounts,
  onDateClick,
}: {
  activeDate: { year: number; month: number };
  holidayDates: string[];
  availableShifts: type.Type_availableShifts[];
  formattedCalendarData: CalendarData;
  selectedCalendarData: Type_calendar;
  setSelectedCalendarData: (
    value: Type_calendar | ((prev: Type_calendar) => Type_calendar)
  ) => void;
  submittedStatus: string;

  selectedShiftType: string;
  isExistWanted: boolean;
  myWeeklyOff?: number | null;
  enableHighlightNew?: boolean;
  enableCommentInput?: boolean; // 원티드 작성 > 캔린더에 사유 입력을 위함.
  onShiftChangeRequest?: (dateStr: string, shiftType: string) => void;
  dailyMaxRequests?: Record<string, number>;
  dailyRequestCounts?: Record<string, number>;
  onDateClick?: (dateStr: string) => void;
}) {
  const firstDay = new Date(activeDate.year, activeDate.month - 1, 1);
  const lastDay = new Date(activeDate.year, activeDate.month, 0);
  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay(); // 한 주 시작 : 일
  startDay = (startDay + 6) % 7; // 한 주 시작 : 월

  const nextMonthDay = new Date(activeDate.year, activeDate.month, 1).getDay();

  // 주휴 추가하기 위함
  const displayData = useMemo(() => {
    const data: Type_calendar = { ...selectedCalendarData };
    if (myWeeklyOff != null) {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(activeDate.year, activeDate.month - 1, day);
        const dayOfWeek = (date.getDay() + 6) % 7; // 월(0) ~ 일(6)
        if (dayOfWeek === myWeeklyOff) {
          const dateStr = `${activeDate.year}-${String(
            activeDate.month
          ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          // 이미 근무 코드가 설정되어 있더라도 주휴에 해당하면 주휴 정보로 덮어쓰도록
          data[dateStr] = { type: "주", comment: "주휴" };
        }
      }
    }
    return data;
  }, [
    selectedCalendarData,
    myWeeklyOff,
    daysInMonth,
    activeDate.year,
    activeDate.month,
  ]);

  const weeks: React.JSX.Element[][] = [];
  let currentWeek: React.JSX.Element[] = [];

  for (let i = 0; i < startDay; i++) {
    const prevDate = new Date(firstDay);
    prevDate.setDate(prevDate.getDate() - (startDay - i));
    const day = prevDate.getDate();
    const weekend = prevDate.getDay() % 7;

    currentWeek.push(
      <td
        className={`other-month ${weekend === 6 ? "calendar_sat" : weekend === 0 ? "calendar_sun" : ""}`}
        key={`prev-${i}`}
      >
        <div className="calendar-date">{day}</div>
      </td>
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    // const shift = selectedCalendarData ? selectedCalendarData[dateStr] : undefined;
    // 주휴 추가
    const shift = displayData ? displayData[dateStr] : undefined;
    const weekend = (day + startDay) % 7;

    const savedShift = formattedCalendarData?.[dateStr];
    const isNew =
      enableHighlightNew && shift
        ? !savedShift || savedShift.shift !== shift.type
        : false;

    const isHol = holidayDates.includes(dateStr);
    currentWeek.push(
      <td key={dateStr}>
        <Card_date
          day={day}
          holiday={{ weekend: weekend, isHol: isHol }}
          dateStr={dateStr}
          availableShifts={availableShifts}
          shift={shift}
          setSelectedCalendarData={setSelectedCalendarData}
          selectedShiftType={selectedShiftType}
          submittedStatus={submittedStatus}
          isExistWanted={isExistWanted}
          isNew={isNew}
          enableCommentInput={enableCommentInput}
          onShiftChangeRequest={onShiftChangeRequest}
          maxCount={dailyMaxRequests?.[dateStr]}
          currentCount={dailyRequestCounts?.[dateStr] || 0}
          onDateClick={onDateClick}
        />
      </td>
    );

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  const remainingCells = 7 - currentWeek.length;

  if (remainingCells > 0) {
    {
      Array.from({ length: remainingCells }, (_, i) => {
        const weekend = (nextMonthDay + i) % 7;
        currentWeek.push(
          <td
            key={`empty-${i + 1}`}
            className={`other-month ${weekend === 6 ? "calendar_sat" : weekend === 0 ? "calendar_sun" : ""}`}
          >
            <div className="calendar-date">{i + 1}</div>
          </td>
        );
      });
    }
    weeks.push(currentWeek);
  }

  // 부모에서 하도록 : 처음 렌더시 selectedCalendarData가 {} 빈객체로 들어와서
  // useEffect(() => {
  //     const converted = Object.fromEntries(
  //         Object.entries(formattedCalendarData).map(([date, value]) => {
  //             const { shift, ...rest } = value;
  //             return [date, { ...rest, type: shift }];
  //         })
  //     );
  //     setSelectedCalendarData(converted);
  // }, [formattedCalendarData])
  return (
    <table className="w_myWanted__calendar w_myWanted__selectCalendar ">
      <thead>
        <tr>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            월
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            화
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            수
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            목
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            금
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sat !text-[15px]"
            )}
          >
            토
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sun !text-[15px]"
            )}
          >
            일
          </th>
        </tr>
      </thead>
      <tbody className="calendar-body">
        {weeks.map((week, idx) => (
          <tr key={idx}>{week}</tr>
        ))}
      </tbody>
    </table>
  );
}

export default TableSelectDate;
